import { useQuery } from '@tanstack/react-query'

import { isHttpError } from '@sparkai/utils'

import { apiClient } from '../../apiClient'

type MetricSummary = {
  current?: number | null
  previous?: number | null
}

type ProgramVolumeDetailedBreakdown = {
  totalCount?: number
  withinTarget?: number
  missedTarget?: number
  engagementCount?: number
  resolvedCount?: number
  timedOutCount?: number
  avgTimeToClose: number
}

type ProgramVolumeMetrics = {
  program: {
    name: string
    displayName: string | null
    targetSpeed: number | null
  }
  engagementCount: MetricSummary
  withinTargetCount: MetricSummary
  resolvedCount: MetricSummary
  avgResolutionTime: MetricSummary
  avgTasksPerEngagement: MetricSummary
  breakdown: {
    [date: string]: ProgramVolumeDetailedBreakdown
  }
}

type Priority = 'high' | 'low'
type Granularity = 'daily' | 'monthly' | 'weekly'

type ProgramVolumeMetricsParams = {
  programId?: string
  period?: { start: string; end: string }
  priority?: Priority
  granularity?: Granularity
  scope?: 'live'
}

export const PROGRAM_VOLUME_STATS_KEY = 'program-volume-stats'

export const shouldRetryProgramVolume = (failureCount: number, error: Error) =>
  !(isHttpError(error) && error.response?.status === 404) && failureCount < 3

export function useGetVolume(params: ProgramVolumeMetricsParams) {
  return useQuery({
    queryKey: [
      PROGRAM_VOLUME_STATS_KEY,
      params.programId,
      params.period?.start,
      params.period?.end,
      params.priority,
      params.granularity,
      params.scope,
    ],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramVolumeMetrics>(
        `/int/v3/programs/${params.programId}/metrics/volume`,
        {
          params: {
            startDate: params.period?.start,
            endDate: params.period?.end,
            priority: params.priority,
            granularity: params.granularity,
            ...(params.scope ? { scope: params.scope } : {}),
          },
        }
      )

      return data
    },
    retry: shouldRetryProgramVolume,
    enabled: Boolean(params.programId) && Boolean(params.period) && Boolean(params.priority),
  })
}
